import React, {
  forwardRef,
  Fragment,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Breadcrumbs } from "../../AbstractElements";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { Table, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { getSaleInfo, getReturnInfo, getPresPreSignedUrl } from "./SalesService";
import { useReactToPrint } from "react-to-print";
import { ArrowLeft } from "react-feather";
import { useCurrency } from "../../_helper/CurrencyContext";
// import { secureApiRequest } from "../../Services/secureApiRequest";
// import { decryptResponseData } from "../../Services/decryptApiResponse";

// Helper function to safely format numbers
const safeToFixed = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return "0.00";
  const num = Number(value);
  if (isNaN(num)) return "0.00";
  return num.toFixed(decimals);
};

// Helper function to safely convert to number
const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

// ✅ Printable section wrapped with forwardRef
const PrintableContent = forwardRef(({ sale, t, formatDate, taxSummary }, ref) => {
  const { getCurrencySymbol, currency, convertCurrency } = useCurrency();

  // Reconstruct taxable values from stored subTotal and taxPercentage,
  // to match how the backend originally calculated discount/tax.
  const totalTaxableAmt = sale.items?.length
    ? sale.items.reduce((sum, item) => {
      const subTotal = safeNumber(item.subTotal); // inclusive of tax
      const rate = safeNumber(item.taxPercentage);
      if (rate <= 0) return sum + subTotal;
      const taxable = subTotal / (1 + rate / 100);
      return sum + taxable;
    }, 0)
    : 0;

  const discountAmount =
    sale.discountType === "percentage"
      ? (totalTaxableAmt * safeNumber(sale.discount)) / 100
      : safeNumber(sale.discount);

  const netTaxableAmt = totalTaxableAmt - discountAmount;

  return (
    <div ref={ref}>
      <Row className="mb-4">
        <Col md="8">
          <h4 className="mb-1 fw-bold">
            {t("Invoice No")}: {sale.salesNumber}&nbsp;
          </h4>
          <div>
            {t("Invoice Date")}: {formatDate(sale.createdAt)}
          </div>
          <div>
            {t("Payment Type")}: {sale.paymentType}
          </div>
          <div>
            {t("Sale Type")}:&nbsp;
            {sale.salesType === "return" ? (
              <span className="badge bg-danger text-uppercase">
                {sale.salesType}
              </span>
            ) : (
              <span className="badge bg-success text-uppercase">
                {sale.salesType}
              </span>
            )}
          </div>
          {taxSummary && (
            <div className="mt-2">
              <Badge bg={sale.isIGST ? "info" : "primary"}>
                {sale.isIGST ? "IGST (Inter-state)" : "CGST + SGST (Intra-state)"}
              </Badge>
            </div>
          )}
        </Col>
        <Col md="4" className="text-end">
          <h5 className="fw-bold">
            {t("Patient")}: {sale.patientName}
          </h5>
        </Col>
      </Row>

      {/* Items Table */}
      <div className="mb-4 table-responsive">
        <Table bordered hover size="sm">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>{t("Brand Name")}</th>
              <th>{t("Salt Name / Composition")}</th>
              <th>{t("Batch No")}</th>
              <th className="text-center">{t("Qty")}</th>
              <th className="text-center">{t("Unit Price")}</th>
              <th className="text-center">{t("Sale Price")}</th>
              <th className="text-center">{t("Taxable Amount")}</th>
              <th className="text-center">{t("Tax %")}</th>
              {sale.isIGST ? (
                <th className="text-center">{t("IGST Amt")}</th>
              ) : (
                <>
                  <th className="text-center">{t("CGST Amt")}</th>
                  <th className="text-center">{t("SGST Amt")}</th>
                </>
              )}
              <th className="text-center">{t("Total Tax")}</th>
              <th className="text-center">{t("Total")}</th>
            </tr>
          </thead>
          <tbody>
            {sale.items.map((item, index) => {
              // Safely convert all tax values to numbers
              const cgst = safeNumber(item.CGST);
              const sgst = safeNumber(item.SGST);
              const igst = safeNumber(item.IGST);
              const tax = safeNumber(item.tax);
              const unitPrice = safeNumber(item.unitPrice);
              const unitPricePerMedicine = safeNumber(item.unitPricePerMedicine);
              const totalPrice = safeNumber(item.totalPrice);
              const taxPercentage = safeNumber(item.taxPercentage);
              const subTotal = safeNumber(item.subTotal);

              const taxableAmount = subTotal / (1 + taxPercentage / 100);
              const discountAmount = safeNumber(item.discount) > 0 ? taxableAmount * (safeNumber(item.discount) / 100) : 0;
              const netTaxableAmount = taxableAmount - discountAmount;
              // console.log(taxableAmount.toFixed(2)); 
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.brandName}</td>
                  <td>{item.medicineName}</td>
                  <td>{item.batchNumber}</td>
                  <td className="text-center">{item.medicineQunatity}</td>
                  <td className="text-center">
                    {getCurrencySymbol()}{safeToFixed(unitPrice)}
                  </td>
                  <td className="text-center">
                    {getCurrencySymbol()}{safeToFixed(unitPricePerMedicine)}
                  </td>
                  <td className="text-center">
                    {/* {getCurrencySymbol()}{safeToFixed(totalPrice - tax)} */}
                    {getCurrencySymbol()}{safeToFixed(netTaxableAmount)}
                  </td>
                  <td className="text-center">{safeToFixed(taxPercentage, 0)}%</td>
                  {sale.isIGST ? (
                    <td className="text-center text-success">
                      {getCurrencySymbol()}{safeToFixed(igst)}
                    </td>
                  ) : (
                    <>
                      <td className="text-center text-success">
                        {getCurrencySymbol()}{safeToFixed(cgst)}
                      </td>
                      <td className="text-center text-success">
                        {getCurrencySymbol()}{safeToFixed(sgst)}
                      </td>
                    </>
                  )}
                  <td className="text-center text-success fw-bold">
                    {getCurrencySymbol()}{safeToFixed(tax)}
                  </td>
                  <td className="text-center fw-bold">
                    {getCurrencySymbol()}{safeToFixed(totalPrice)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <Row
      >
        {/* Tax Summary Section */}
        <Col md="12" lg="6">
          {taxSummary && taxSummary.length > 0 && (
            <>
              <h5 className="fw-bold text-primary mb-3">{t("Tax Summary")}</h5>
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>{t("GST %")}</th>
                    <th className="text-end">{t("Taxable Value")}</th>
                    {safeNumber(sale.discount) > 0 && (
                      <th className="text-end">{t("Taxable Value (Discounted)")}</th>
                    )}
                    {sale.isIGST ? (
                      <th className="text-end">{t("IGST Amt")}</th>
                    ) : (
                      <>
                        <th className="text-end">{t("CGST Amt")}</th>
                        <th className="text-end">{t("SGST Amt")}</th>
                      </>
                    )}
                    <th className="text-end">{t("Total Tax")}</th>
                    <th className="text-end">{t("Net Amt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {taxSummary.map((taxGroup, index) => (
                    <tr key={index}>
                      <td className="text-center fw-bold">{safeToFixed(taxGroup.rate, 0)}%</td>
                      <td className="text-end">
                        {getCurrencySymbol()}
                        {safeToFixed(taxGroup.taxableBeforeDiscount)}
                      </td>
                      {safeNumber(sale.discount) > 0 && (
                        <td className="text-end">
                          {getCurrencySymbol()}
                          {safeToFixed(taxGroup.taxableAfterDiscount)}
                        </td>
                      )}
                      {sale.isIGST ? (
                        <td className="text-end text-success">
                          {getCurrencySymbol()}{safeToFixed(taxGroup.igst)}
                        </td>
                      ) : (
                        <>
                          <td className="text-end text-success">
                            {getCurrencySymbol()}{safeToFixed(taxGroup.cgst)}
                          </td>
                          <td className="text-end text-success">
                            {getCurrencySymbol()}{safeToFixed(taxGroup.sgst)}
                          </td>
                        </>
                      )}
                      <td className="text-end text-success fw-bold">
                        {getCurrencySymbol()}{safeToFixed(taxGroup.totalTax)}
                      </td>
                      <td className="text-end fw-bold">
                        {getCurrencySymbol()}{safeToFixed(taxGroup.netAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Col>
        {/* Totals Section */}
        <Col md="12" lg="6">
          <Table bordered size="sm">
            <tbody>
              <tr>
                <td className="fw-bold">{t("Total Taxable Value")}</td>
                <td className="text-end">
                  {getCurrencySymbol()}{safeToFixed(totalTaxableAmt)}
                </td>
              </tr>

              {safeNumber(sale.discount) > 0 && (
                <>
                  <tr>
                    <td className="fw-bold">
                      {t("Discount")}
                      {sale.discountType === "percentage"
                        ? ` (${safeToFixed(sale.discount, 0)}%)`
                        : " (Fixed)"}
                    </td>
                    <td className="text-end text-danger">
                      - {getCurrencySymbol()}{discountAmount.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">
                      {t("Net Taxable Value")}
                    </td>
                    <td className="text-end">
                      {getCurrencySymbol()}{safeToFixed(netTaxableAmt)}
                    </td>
                  </tr>
                </>
              )}

              {/* Tax Breakdown */}
              {sale.isIGST ? (
                <tr>
                  <td className="fw-bold">
                    {t("Add: IGST")} ({safeToFixed(sale.IGSTPercentage, 0)}%)
                  </td>
                  <td className="text-end text-success">
                    + {getCurrencySymbol()}{safeToFixed(sale.IGST)}
                  </td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td className="fw-bold">
                      {t("Add: CGST")} ({safeToFixed(sale.CGSTPercentage, 0)}%)
                    </td>
                    <td className="text-end text-success">
                      + {getCurrencySymbol()}{safeToFixed(sale.CGST)}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">
                      {t("Add: SGST")} ({safeToFixed(sale.SGSTPercentage, 0)}%)
                    </td>
                    <td className="text-end text-success">
                      + {getCurrencySymbol()}{safeToFixed(sale.SGST)}
                    </td>
                  </tr>
                </>
              )}

              <tr className="fw-bold">
                <td>{t("Total Tax")}</td>
                <td className="text-end text-success">
                  {getCurrencySymbol()}{safeToFixed(sale.tax)}
                </td>
              </tr>

              <tr className="fw-bold">
                <td>{t("Paid in")}</td>
                <td className="text-end">{sale.currencyType}</td>
              </tr>
              <tr className="table-primary fw-bold fs-5">
                <td>{t("Grand Total")}</td>
                <td className="text-end">
                  <div className="d-flex flex-column">
                    {currency !== sale.currencyType && (
                      <span className="tw-text-xs text-muted">
                        {getCurrencySymbol()}{safeToFixed(convertCurrency(sale.netTotal, sale.currencyType, currency))}
                      </span>
                    )}
                    {getCurrencySymbol(sale.currencyType)}{safeToFixed(sale.netTotal)}
                  </div>
                </td>
              </tr>

              <tr className="table-success fw-bold">
                <td>{t("Paid")}</td>
                <td className="text-end">
                  {sale.currencyType === "USD" ? "$" : ""}
                  {safeToFixed(sale.totalPaid)}
                  {sale.currencyType === "ZiG" ? " ZiG" : ""}
                </td>
              </tr>

              {sale.salesType === "sales" && safeNumber(sale.dueAmount) !== 0 && (
                <tr className="table-warning fw-bold">
                  <td>{t("Due Amount")}</td>
                  <td className="text-end">
                    {getCurrencySymbol()}{safeToFixed(sale.dueAmount)}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  )
});

const ViewSale = () => {
  const { t } = useTranslation();
  const { saleId } = useParams();
  const { getCurrencySymbol } = useCurrency();
  const [sale, setSale] = useState(null);
  const [returnedPurchases, setReturnedPurchases] = useState([]);
  const { setLoading } = useLoader();
  const { pharmacyPOS } = usePharmacy();
  const navigate = useNavigate();

  const printRef = useRef();

  const [prescriptionUrl, setPrescriptionUrl] = useState("");

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Invoice_${sale?.salesNumber}`,
    pageStyle: `
			.receipt-container {
				padding: 10px 5px; /* extra space inside the receipt */
			}
    `,
  });

  useEffect(() => {
    const fetchSale = async () => {
      try {
        setLoading(true);
        const body = {
          clinicId: pharmacyPOS?.hospital_id,
          salesId: saleId,
        };
        const saleReturnPayload = {
          salesId: saleId,
        }
        const response = await getSaleInfo(body);
        const saleReturnResponse = await getReturnInfo(saleReturnPayload);
        const data = saleReturnResponse.data.data;
        setReturnedPurchases(data.sales || []);
        if (response.data?.status === "success") {
          setSale(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching sale details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSale();
  }, [saleId]);

  // Calculate tax summary grouped by tax percentage
  const taxSummary = useMemo(() => {
    if (!sale?.items) return [];

    // Group items by tax percentage
    const taxGroups = sale.items.reduce((acc, item) => {
      const rate = safeNumber(item.taxPercentage);
      if (!acc[rate]) {
        acc[rate] = {
          rate,
          taxableBeforeDiscount: 0,
          taxableAfterDiscount: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          totalTax: 0,
          netAmount: 0
        };
      }

      const subTotal = safeNumber(item.subTotal); // inclusive line total
      const totalPrice = safeNumber(item.totalPrice);
      const tax = safeNumber(item.tax);
      const rateValue = rate > 0 ? rate : 0;

      // Taxable before any bill-level discount (derived from subTotal)
      const taxableBefore =
        rateValue > 0 ? subTotal / (1 + rateValue / 100) : subTotal;

      const cgst = safeNumber(item.CGST);
      const sgst = safeNumber(item.SGST);
      const igst = safeNumber(item.IGST);

      acc[rate].taxableBeforeDiscount += taxableBefore;
      acc[rate].cgst += cgst;
      acc[rate].sgst += sgst;
      acc[rate].igst += igst;
      acc[rate].totalTax += tax;
      acc[rate].netAmount += totalPrice;

      return acc;
    }, {});

    const groups = Object.values(taxGroups);

    // Distribute bill-level discount across tax groups based on
    // their share of the taxable-before-discount.
    const totalTaxableBefore = groups.reduce(
      (sum, g) => sum + g.taxableBeforeDiscount,
      0
    );

    const discountAmount =
      sale.discountType === "percentage"
        ? (totalTaxableBefore * safeNumber(sale.discount)) / 100
        : safeNumber(sale.discount);

    groups.forEach((g) => {
      const proportion =
        totalTaxableBefore > 0 ? g.taxableBeforeDiscount / totalTaxableBefore : 0;
      const groupDiscount = discountAmount * proportion;
      g.taxableAfterDiscount = Math.max(
        0,
        g.taxableBeforeDiscount - groupDiscount
      );
    });

    // Convert to array and sort by rate
    return groups.sort((a, b) => a.rate - b.rate);
  }, [sale]);

  if (!sale) {
    return <div className="py-5 text-center">{t("Loading...")}</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format returned purchases items with proper tax display
  const formatReturnItems = (returnSale) => {
    return returnSale.items?.map(item => ({
      ...item,
      // Ensure tax values are properly converted to numbers
      tax: safeNumber(item.tax),
      taxPercentage: safeNumber(item.taxPercentage),
      isIGST: item.isIGST || false,
      IGST: safeNumber(item.IGST),
      CGST: safeNumber(item.CGST),
      SGST: safeNumber(item.SGST)
    })) || [];
  };

  // const extractS3Key = (url) => {
  //   if (!url) return "";
  //   return url.split(".com/")[1]; // UserPrescription/xxx.pdf
  // };


  const getPrescriptionPresignedURL = async () => {
    try {
      setLoading(true);

      const key = sale?.prescription;
      if (!key) return;

      // const response = await secureApiRequest(
      //   "post",
      //   "/pharma/getUploadFilepresignedURL",
      //   { key }
      // );

      const response = await getPresPreSignedUrl({ key });
      // const dec = decryptResponseData(response);
      const url = response.data.data?.presignedURL;

      setPrescriptionUrl(url);
      if (prescriptionUrl) {
        window.open(prescriptionUrl, "_blank");
      }
      // setShowPrescription(true);
    } catch (err) {
      console.error("Failed to load prescription", err);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={t("View Sale")}
        parent={t("Dashboard")}
        title={t("View Sale")}
      />

      <div className="gap-2 mb-3 d-flex justify-content-end">
        <Button
          color="primary"
          size="sm"
          className="d-flex align-items-center justify-content-center"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/sales-history`)}
        >
          <ArrowLeft size={16} className="me-1" />
          {t("Back to Sale History")}
        </Button>
        <Button color="primary" onClick={handlePrint}>
          <i className="fa fa-print"></i> {t("Print")}
        </Button>
        {sale.prescription && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={getPrescriptionPresignedURL}
          >
            <i className="fa fa-file-medical me-1" />
            {t("View Prescription")}
          </Button>
        )}
      </div>

      <Col sm="12">
        <Card>
          <CardBody className="receipt-container">
            {/* ✅ Now using forwardRef wrapped component */}
            <PrintableContent
              ref={printRef}
              sale={sale}
              t={t}
              formatDate={formatDate}
              taxSummary={taxSummary}
            />

            {/* Returned Purchases */}
            {returnedPurchases.length > 0 && (
              <div className="mt-5">
                <h5 className="fw-bold text-danger mb-4">{t("Return History")}</h5>

                {returnedPurchases.map((ret) => {
                  const isReturnIGST = ret.isIGST || false;
                  const formattedItems = formatReturnItems(ret);

                  // Reconstruct taxable values and discount for each return sale
                  const retTotalTaxable = ret.items?.length
                    ? ret.items.reduce((sum, item) => {
                        const subTotal = safeNumber(item.subTotal); // inclusive line total
                        const rate = safeNumber(item.taxPercentage);
                        if (rate <= 0) return sum + subTotal;
                        const taxable = subTotal / (1 + rate / 100);
                        return sum + taxable;
                      }, 0)
                    : 0;

                  return (
                    <Card className="mb-4 border-danger" key={ret._id}>
                      <CardBody>
                        {/* Top section with return details */}
                        <Row className="mb-3">
                          <Col md="8">
                            <div className="d-flex align-items-center gap-2">
                              <strong className="text-danger">{t("Return No")}:</strong>
                              <span>{ret.salesNumber}</span>
                              <Badge bg="danger">{t("RETURN")}</Badge>
                              <Badge bg={isReturnIGST ? "info" : "primary"}>
                                {isReturnIGST ? "IGST" : "CGST+SGST"}
                              </Badge>
                            </div>
                            <div>
                              <strong>{t("Return Date")}:</strong> {formatDate(ret.createdAt)}
                            </div>
                            <div>
                              <strong>{t("Customer")}:</strong> {ret.patientName}
                            </div>
                          </Col>

                          <Col md="4" className="text-end">
                            <div className="text-danger fw-bold fs-5">
                              {t("Total Return Amount")}: {getCurrencySymbol()}{safeToFixed(ret.netTotal)}
                            </div>
                            <div className="text-muted small">
                              {t("Tax")}: {getCurrencySymbol()}{safeToFixed(ret.tax)}
                            </div>
                          </Col>
                        </Row>

                        {/* Items Table */}
                        <div className="table-responsive mb-3">
                          <Table bordered size="sm" className="align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>{t("Brand Name")}</th>
                                <th>{t("Batch No")}</th>
                                <th className="text-center">{t("Returned Qty")}</th>
                                <th className="text-center">{t("Sale Price")}</th>
                                <th className="text-center">{t("Tax %")}</th>
                                {isReturnIGST ? (
                                  <th className="text-center">{t("IGST Amt")}</th>
                                ) : (
                                  <>
                                    <th className="text-center">{t("CGST Amt")}</th>
                                    <th className="text-center">{t("SGST Amt")}</th>
                                  </>
                                )}
                                <th className="text-center">{t("Total Tax")}</th>
                                <th className="text-center">{t("Total")}</th>
                              </tr>
                            </thead>

                            <tbody>
                              {formattedItems.map((item, i) => (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>{item.brandName || item.medicineName || "-"}</td>
                                  <td>{item.batchNumber}</td>
                                  <td className="text-center">{item.medicineQunatity}</td>
                                  <td className="text-center">
                                    {getCurrencySymbol()}{safeToFixed(item.unitPricePerMedicine)}
                                  </td>
                                  <td className="text-center">{safeToFixed(item.taxPercentage, 0)}%</td>
                                  {isReturnIGST ? (
                                    <td className="text-center text-success">
                                      {getCurrencySymbol()}{safeToFixed(item.IGST)}
                                    </td>
                                  ) : (
                                    <>
                                      <td className="text-center text-success">
                                        {getCurrencySymbol()}{safeToFixed(item.CGST)}
                                      </td>
                                      <td className="text-center text-success">
                                        {getCurrencySymbol()}{safeToFixed(item.SGST)}
                                      </td>
                                    </>
                                  )}
                                  <td className="text-center text-success fw-bold">
                                    {getCurrencySymbol()}{safeToFixed(item.tax)}
                                  </td>
                                  <td className="text-center fw-bold text-danger">
                                    {getCurrencySymbol()}{safeToFixed(item.totalPrice)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        {/* Return Summary */}
                        <Row className="align-items-end">
                          <Col md="8">
                            <Table bordered size="sm" className="mb-3">
                              <thead className="table-light">
                                <tr>
                                  <th className="text-center">{t("Summary")}</th>
                                  <th className="text-end">{t("Taxable Amount")}</th>
                                  {isReturnIGST ? (
                                    <th className="text-end">{t("IGST")}</th>
                                  ) : (
                                    <>
                                      <th className="text-end">{t("CGST")}</th>
                                      <th className="text-end">{t("SGST")}</th>
                                    </>
                                  )}
                                  <th className="text-end">{t("Total Tax")}</th>
                                  <th className="text-end">{t("Net Amount")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="text-center fw-bold">{t("Return Summary")}</td>
                                  <td className="text-end">
                                    {getCurrencySymbol()}{safeToFixed(retTotalTaxable)}
                                  </td>
                                  {isReturnIGST ? (
                                    <td className="text-end text-success">
                                      {getCurrencySymbol()}{safeToFixed(ret.IGST)}
                                    </td>
                                  ) : (
                                    <>
                                      <td className="text-end text-success">
                                        {getCurrencySymbol()}{safeToFixed(ret.CGST)}
                                      </td>
                                      <td className="text-end text-success">
                                        {getCurrencySymbol()}{safeToFixed(ret.SGST)}
                                      </td>
                                    </>
                                  )}
                                  <td className="text-end fw-bold text-success">
                                    {getCurrencySymbol()}{safeToFixed(ret.tax)}
                                  </td>
                                  <td className="text-end fw-bold text-danger">
                                    {getCurrencySymbol()}{safeToFixed(ret.netTotal)}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Col>
                          <Col md="4">
                            <Table bordered size="sm">
                              <tbody>
                                {isReturnIGST ? (
                                  <tr>
                                    <td className="text-end">{t("IGST")}:</td>
                                    <td className="text-end text-success fw-bold">
                                      {getCurrencySymbol()}{safeToFixed(ret.IGST)}
                                    </td>
                                  </tr>
                                ) : (
                                  <>
                                    <tr>
                                      <td className="text-end">{t("CGST")}:</td>
                                      <td className="text-end text-success fw-bold">
                                        {getCurrencySymbol()}{safeToFixed(ret.CGST)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-end">{t("SGST")}:</td>
                                      <td className="text-end text-success fw-bold">
                                        {getCurrencySymbol()}{safeToFixed(ret.SGST)}
                                      </td>
                                    </tr>
                                  </>
                                )}
                                <tr className="table-danger fw-bold fs-5">
                                  <td className="text-end">{t("RETURN TOTAL")}</td>
                                  <td className="text-end text-danger">
                                    {getCurrencySymbol()}{safeToFixed(ret.netTotal)}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Fragment>
  );
};

export default ViewSale;